import { verifyAsync } from "@noble/ed25519";
import type { WalletStandardAccount, WalletStandardWallet } from "@solana/connector/react";
import { Base58Util } from "@solomon-labs/base58";

export interface WalletMessageProof {
    signature: string;
    /** Base58 of the exact bytes signed. Omitted for an unchanged raw challenge. */
    signedMessage?: string;
}

// ConnectorKit 0.2.1 predates this feature. Keep this boundary aligned with
// @solana/wallet-standard-features/src/signOffchainMessage.ts (API 1.0.0).
interface OffchainFeature {
    version: "1.0.0";
    supportedMessageVersions: readonly number[];
    signOffchainMessage(input: {
        account: WalletStandardAccount;
        messageVersion: 1;
        message: string;
        requiredSigners: readonly Uint8Array[];
    }): Promise<readonly { signature: Uint8Array; signedOffchainMessage: Uint8Array; signatureType?: string }[]>;
}

interface MessageFeature {
    signMessage(input: {
        account: WalletStandardAccount;
        message: Uint8Array;
    }): Promise<readonly { signature: Uint8Array; signedMessage: Uint8Array; signatureType?: string }[]>;
}

interface LegacyMessagePreparer {
    prepareSolanaOffchainMessage(
        message: Uint8Array,
        encoding: "ASCII" | "UTF-8",
        maxLength: 1212
    ): Promise<Uint8Array>;
}

function legacyPreparer(wallet: WalletStandardWallet): LegacyMessagePreparer | null {
    // Backpack exposes its existing provider through Wallet Standard. Inspect
    // the method, not the wallet name, and never reach into window globals.
    const feature = wallet.features["backpack:"];
    if (typeof feature !== "object" || feature === null || !("backpack" in feature)) return null;
    const provider = feature.backpack;
    return typeof provider === "object" &&
        provider !== null &&
        "prepareSolanaOffchainMessage" in provider &&
        typeof provider.prepareSolanaOffchainMessage === "function"
        ? (provider as LegacyMessagePreparer)
        : null;
}

function isOffchainFeature(value: unknown): value is OffchainFeature {
    return (
        typeof value === "object" &&
        value !== null &&
        "version" in value &&
        value.version === "1.0.0" &&
        "supportedMessageVersions" in value &&
        Array.isArray(value.supportedMessageVersions) &&
        value.supportedMessageVersions.includes(1) &&
        "signOffchainMessage" in value &&
        typeof value.signOffchainMessage === "function"
    );
}

function isMessageFeature(value: unknown): value is MessageFeature {
    return (
        typeof value === "object" && value !== null && "signMessage" in value && typeof value.signMessage === "function"
    );
}

/** Compatibility with wallets which wrap a raw challenge in compact v0 but
 * return the original input as signedMessage. No second signing prompt. */
function legacyMessage(message: Uint8Array, format: 0 | 1): Uint8Array {
    return new Uint8Array([
        255,
        ...new TextEncoder().encode("solana offchain"),
        0,
        format,
        message.length & 255,
        (message.length >> 8) & 255,
        ...message
    ]);
}

/** Shared by every wallet brand. Each account advertises its own capabilities.
 * A rejected/failed request propagates; it never triggers another wallet prompt. */
export async function signVerificationMessage(
    wallet: WalletStandardWallet,
    address: string,
    message: string
): Promise<WalletMessageProof> {
    const account = wallet.accounts.find((candidate) => candidate.address === address);
    if (!account) throw new Error("Wallet account changed before message signing");
    const messageBytes = new TextEncoder().encode(message);
    const offchain = wallet.features["solana:signOffchainMessage"];
    const raw = wallet.features["solana:signMessage"];
    let signature: Uint8Array;
    let signedBytes: Uint8Array;
    let signatureType: string | undefined;
    if (account.features.includes("solana:signOffchainMessage") && isOffchainFeature(offchain)) {
        const outputs = await offchain.signOffchainMessage({
            account,
            messageVersion: 1,
            message,
            requiredSigners: [new Uint8Array(account.publicKey)]
        });
        if (outputs.length !== 1) throw new Error("Wallet returned an invalid message signature response");
        ({ signature, signedOffchainMessage: signedBytes, signatureType } = outputs[0]);
    } else if (account.features.includes("solana:signMessage") && isMessageFeature(raw)) {
        const prepare = legacyPreparer(wallet);
        let input: Uint8Array = messageBytes;
        if (prepare && messageBytes.length <= 1212) {
            const ascii = messageBytes.every((byte) => byte >= 32 && byte <= 126);
            const prepared = await prepare.prepareSolanaOffchainMessage(messageBytes, ascii ? "ASCII" : "UTF-8", 1212);
            const expected = legacyMessage(messageBytes, ascii ? 0 : 1);
            if (
                !(prepared instanceof Uint8Array) ||
                prepared.length !== expected.length ||
                !prepared.every((byte, index) => byte === expected[index])
            ) {
                throw new Error("Wallet prepared an unexpected off-chain message");
            }
            input = prepared;
        }
        const outputs = await raw.signMessage({ account, message: input });
        if (outputs.length !== 1) throw new Error("Wallet returned an invalid message signature response");
        ({ signature, signedMessage: signedBytes, signatureType } = outputs[0]);
    } else {
        throw new Error("Wallet does not support message signing");
    }
    if (
        !(signature instanceof Uint8Array) ||
        signature.length !== 64 ||
        !(signedBytes instanceof Uint8Array) ||
        signedBytes.length === 0 ||
        signedBytes.length > 8192 ||
        (signatureType !== undefined && signatureType !== "ed25519")
    ) {
        throw new Error("Wallet returned an invalid message signature response");
    }
    let valid = await verifyAsync(signature, signedBytes, new Uint8Array(account.publicKey));
    if (!valid && messageBytes.length <= 1212) {
        // The API also binds the decoded body to its original single-use challenge.
        for (const format of [0, 1] as const) {
            if (format === 0 && !messageBytes.every((byte) => byte >= 32 && byte <= 126)) continue;
            const wrapped = legacyMessage(messageBytes, format);
            if (await verifyAsync(signature, wrapped, new Uint8Array(account.publicKey))) {
                signedBytes = wrapped;
                valid = true;
                break;
            }
        }
    }
    if (!valid) throw new Error("Wallet signature does not match the signed message");
    const unchanged =
        signedBytes.length === messageBytes.length && signedBytes.every((byte, index) => byte === messageBytes[index]);
    return {
        signature: Base58Util.encode(signature),
        ...(!unchanged ? { signedMessage: Base58Util.encode(signedBytes) } : {})
    };
}
